// ProductCard — product tile for catalog grid.
// Shows the first product image (TD-270), name, product-type tag, a short
// blurb, and a representative price. Inline styles only — no Tailwind.
//
// R2 catalog-display redesign (fix round 1, Ruling 11): the catalog LIST
// response now also carries `price.components[]` (Task 4) alongside
// `charges[]` — this tile reads it via `formatPriceHeadline` (same
// formatter the PDP uses, `product-display.ts`) so the tile and the PDP
// never show a DIFFERENT headline for the same cell-priced product; falls
// back to the legacy `deriveDisplayPrice(charges[])` path only when `price`
// is absent. NOTE: `ProductCard` is also consumed by the PORTAL
// (StoreContentPreviewPage -> StorePreview -> ProductGrid -> ProductCard,
// fed by `fetchPublicCatalog`'s raw cast of the same wire shape) — this
// file is the only one touched for that consequence; the portal tree itself
// is out of scope here (Task 6 owns it).
//
// Pre-R2: the catalog wire shape carries `images[]` (TD-270) but no flat
// `price` (project rule §39): pricing is derived from `charges[]` via
// deriveDisplayPrice. Spec defect D4: the media area is ALWAYS a fixed 4:3
// box (`data-testid="product-card-media"`) with an identical inline style
// whether or not the product has a thumbnail, so the catalog grid never
// jumps/collapses/raggeds across cards. When the image URL fails to load,
// the operator-configured marketplace placeholder (TD-324) is attempted
// next; only if that is absent or also fails does a branded placeholder
// (built from `t.colors`, decorative — `aria-hidden`) fill the box. A
// product with no images of its own is substituted server-side.

import { useState, useId } from "react";
import { useEmbedTokens, EmbedBadge } from "../../embed-shared/primitives";
import { useReducedMotion, motionSafe } from "../../embed-shared/theme";
import type { Product } from "../../embed-shared/types";
import { stripMarkdown } from "../../embed-shared/markdown";
import { AttentionChip } from "./attention-banner";
import { PricingDisplay } from "./PricingDisplay";
import { deriveDisplayPrice, formatPriceHeadline } from "./product-display";
import { useStorefrontConfig } from "./storefront-config";

// D1 canonical product-type labels (the TD-335 fix): hardware / software /
// service are the catalog's canonical product types and render exactly these
// labels. Any other value (legacy "physical"/"subscription", or a future type
// the operator invents) falls back to a TITLE-CASED rendering of the raw
// value — the customer must never see the raw lowercase string. Exported so
// the catalog rail's type chips (CatalogView) render the identical labels.
const TYPE_LABELS: Record<string, string> = {
  hardware: "Hardware",
  software: "Software",
  service: "Service",
};

export function typeLabel(productType: string): string {
  // charAt (not [0]) so an empty string degrades to "" instead of throwing.
  return TYPE_LABELS[productType] ?? productType.charAt(0).toUpperCase() + productType.slice(1);
}

export interface ProductCardProps {
  product: Product;
  onClick?: (product: Product) => void;
}

export function ProductCard({ product, onClick }: ProductCardProps) {
  const t = useEmbedTokens();
  const reduced = useReducedMotion();
  // ITEM A fix (fix wave 2026-09-15, operator ruling 2026-09-15): currency is
  // marketplace-wide, never per-product — read once here and forwarded to
  // every price-formatting call below instead of letting formatCurrency's
  // "USD" default apply by accident (formatPriceHeadline) or reading a
  // per-charge wire currency (deriveDisplayPrice's legacy `.currency` —
  // member removed entirely by TD-513 Task 4, 2026-09-19).
  const { currency, placeholder_image_url: placeholderUrl } = useStorefrontConfig();
  const charges = product.charges ?? [];
  const priceComponents = product.price?.components ?? [];
  const hasPriceComponents = priceComponents.length > 0;
  // I2 fix (fix wave 2026-09-15, "server fail-closed and client fail-closed
  // do not compose"): `price` being PRESENT with an EMPTY components[] means
  // the server resolved this product as cell-priced but currently
  // unresolvable (customer_handler.go's resolveCatalogPrice — "priced but
  // unresolvable," never "not cell-priced") and must NOT fall back to
  // charges[], which can be a stale, pre-cell-grid amount for a product
  // genuinely configured to be cell-priced. `hasPriceComponents` alone
  // conflates "absent" and "present-but-empty" into the same falsy check —
  // the price-fallback gate below keys on priceIsAbsent instead, so ONLY a
  // genuinely absent `price` key falls back to the legacy charges[] path.
  const priceIsAbsent = product.price == null;
  const headline = hasPriceComponents ? formatPriceHeadline(product.price, product.charges, currency) : null;
  // Legacy path — used only when `price` is absent (zero behavior change on
  // existing charges[]-only products, mirroring ProductDetailView.tsx).
  const price = deriveDisplayPrice(charges);
  // The type badge's recurring/one-time tone must read from the SAME source
  // as the headline: a cell-priced recurring product's charges[] is empty
  // (Task 4 — real rows only, never synthetic), so deriving this from
  // charges[] alone would badge a recurring product "one-time" right next
  // to a headline reading "$X per month". Unaffected by the I2 fix above —
  // a present-but-empty `price` has no components to read a tone from
  // either way, so it correctly falls through to the charges[]-derived tone.
  const hasRecurring = hasPriceComponents
    ? priceComponents.some((c) => c.charge_type === "recurring")
    : charges.some((c) => c.recurring_amount != null);
  const thumbnail = product.images?.[0];
  // Track failed loads by URL, in a Set rather than a single value (TD-324).
  // Three tiers can collapse to as few as two distinct URLs: when a product
  // has no image of its own, the server has already substituted the
  // operator-configured placeholder into images[0] (is_placeholder: true),
  // so the "real" thumbnail URL and the configured placeholder URL are
  // literally the same string. Keying failures by URL means that shared URL
  // is skipped at every tier once it fails, instead of being re-attempted as
  // a distinct "tier 2" — one error collapses straight to the branded box. A
  // later re-render with a *different* thumbnail URL (e.g. a new product)
  // naturally re-attempts it since it isn't in the Set.
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());

  // Tier 1: the product's own (or server-substituted) image. Tier 2: the
  // operator-configured marketplace-wide placeholder — attempted only when
  // tier 1 is absent/broken. Tier 3 (branded box) is implicit: `src` is
  // undefined when neither candidate survives.
  const src = [thumbnail?.url, placeholderUrl].find(
    (u): u is string => typeof u === "string" && u !== "" && !failed.has(u),
  );
  // Stable id so the focusable <article> is labelled by its own <h3> heading
  // (TD-313). Previously aria-label repeated the name, and the <img> alt fell
  // back to product.name, so a screen reader announced the product name THREE
  // times on a real-image card (article aria-label + img alt + h3). Letting the
  // h3 label the article announces it once; the type badge still reads.
  const headingId = useId();

  return (
    <article
      role="article"
      tabIndex={0}
      onClick={() => onClick?.(product)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.(product);
        }
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: t.borderRadiusLg,
        border: `1px solid ${t.colors.cardBorder}`,
        backgroundColor: t.colors.cardBg,
        boxShadow: t.colors.cardShadow,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: motionSafe(reduced, "box-shadow 0.15s ease"),
      }}
      aria-labelledby={headingId}
    >
      <div
        data-testid="product-card-media"
        style={{ position: "relative", width: "100%", aspectRatio: "4 / 3", overflow: "hidden" }}
      >
        {/* Attention banner corner chip (2026-08-16): pinned top-left with a
            small inset INSIDE the thumbnail box (the box's overflow:hidden +
            border radius clip it; position:relative here is its positioning
            context). zIndex keeps it above the <img> that follows in DOM
            order. Readable tag text — never aria-hidden. */}
        {product.attention_label ? (
          <div style={{ position: "absolute", top: 8, left: 8, zIndex: 1 }}>
            <AttentionChip label={product.attention_label} color={product.attention_color} />
          </div>
        ) : null}
        {src ? (
          <img
            src={src}
            // Tier 1 (the product's real/substituted thumbnail) carries the
            // author's alt text, decorative fallback "" (TD-313). Tier 2
            // (the configured placeholder) is always decorative — it never
            // describes this specific product — so alt is always "".
            alt={src === thumbnail?.url ? (thumbnail.alt_text ?? "") : ""}
            loading="lazy"
            onError={() => setFailed((prev) => new Set(prev).add(src))}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            aria-hidden="true"
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: t.colors.primaryTint,
            }}
          >
            <div
              style={{
                width: t.spacing.xl,
                height: t.spacing.xl,
                borderRadius: t.borderRadius,
                backgroundColor: t.colors.primary,
              }}
            />
          </div>
        )}
      </div>
      <div
        style={{
          padding: t.spacing.md,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        {/* Type badge on its OWN row above the name — previously shared a
            space-between row with the name, which stole width from the name
            and caused ragged wrapping across tiles. */}
        <div style={{ marginBottom: t.spacing.xs }}>
          <EmbedBadge tone={hasRecurring ? "recurring" : "oneTime"}>
            {typeLabel(product.product_type)}
          </EmbedBadge>
        </div>
        {/* Name spans full width — no truncation/ellipsis, wraps naturally. */}
        <h3
          id={headingId}
          style={{
            fontSize: t.fontSize.md,
            fontWeight: 600,
            color: t.colors.text,
            margin: 0,
            lineHeight: t.lineHeight.tight,
            wordBreak: "break-word",
          }}
        >
          {product.name}
        </h3>
        {product.description && (
          <p
            style={{
              fontSize: t.fontSize.sm,
              color: t.colors.textSecondary,
              margin: `0 0 ${t.spacing.sm}px 0`,
              lineHeight: t.lineHeight.normal,
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {/* Flattened, not rendered: this blurb is clamped to two lines, and
                a heading or bullet inside a line-clamp clips mid-element and
                shifts the price row. The PDP renders the same source
                formatted via EmbedMarkdown. */}
            {stripMarkdown(product.description)}
          </p>
        )}
        {/* Price block pinned to bottom — marginTop:auto in a flex column
            pushes this to the bottom edge so prices align across tiles of
            varying name/description heights. */}
        <div style={{ marginTop: "auto" }}>
          {hasPriceComponents && headline ? (
            // R2 catalog-display redesign (fix round 1, Ruling 11): render
            // formatPriceHeadline's already-complete string directly, same
            // decision as ProductDetailView.tsx — decomposing it back into
            // PricingDisplay's separate amount/recurringLabel/isFromPrice
            // props would re-introduce the hand-assembly this formatter
            // exists to remove. `originalAmount`/strikethrough is unused at
            // BOTH this call site and the PDP's today, so nothing is lost.
            <span style={{ fontSize: t.fontSize.lg, fontWeight: 700, color: t.colors.text }}>
              {headline}
            </span>
          ) : priceIsAbsent && price ? (
            // ITEM A fix (fix wave 2026-09-15): `currency` here is the global
            // StorefrontConfig value — historically NOT `price.currency`
            // (deriveDisplayPrice's per-CHARGE currency, sourced from the
            // legacy `charges[].currency` wire field), which TD-513 Task 4
            // (2026-09-19) then removed from the display shape entirely; the
            // ruling retires per-product currency as an authority.
            <PricingDisplay
              amount={price.amount}
              currency={currency}
              recurringLabel={price.recurringLabel}
              isFromPrice={price.isFromPrice}
            />
          ) : (
            <span style={{ fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
              Contact for pricing
            </span>
          )}
        </div>
      </div>
    </article>
  );
}
